import { Component, OnInit, Input } from '@angular/core';
import { User, Company } from '@app/models';
import { LocalStorage } from 'ngx-webstorage';
import { AccountService, CompanyService, SharedService, Toaster } from './../../../../services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserDeletionErrorModalComponent, UserDeletionError } from '@app/shared/modals';
import CustomStore from 'devextreme/data/custom_store';
import Swal from 'sweetalert2';

@Component({
  selector: 'ngx-company-users',
  templateUrl: './company-users.component.html',
  styleUrls: ['./../company.style.scss'],
})
export class CompanyUsersComponent implements OnInit {
  @LocalStorage() organizationId: string;
  @LocalStorage() role: string;

  @Input() company: Company;

  loading: boolean = false;

  dataSource: any;
  rolesData: any;

  constructor(
    private $company: CompanyService,
    private $account: AccountService,
    private $shared: SharedService,
    private $toaster: Toaster,
    private $modal: NgbModal,
  ) {
  }

  ngOnInit() {
    this.dataSource = new CustomStore({
      key: 'id',
      load: () => this.getUsers(),
      insert: (values) => this.addUser(values),
      update: (key, values) => this.updateUser({id: key, ...values}),
      remove: (key) => this.deleteUser(key),
    });

    this.rolesData = {
      store: new CustomStore({
        key: 'name',
        loadMode: 'raw',
        load: () => this.$shared.roles(),
      }),
    };
  }

  getUsers() {
    return (this.$company.users(this.organizationId))
      .toPromise()
      .then((users: any) => users)
      .catch((error) => {
        console.error(error);
        this.$toaster.show('error', 'Users', 'An error occurred, please try again later.');
      });
  }

  updateUser(data: any) {
    return (this.$company.updateUser(this.organizationId, data))
      .toPromise()
      .then((users: any) => {
        this.$toaster.show('success', 'User', 'The user was updated successfully.');
      }).catch((error) => {
        console.error(error);
        this.$toaster.show('error', 'Users', 'An error occurred, please try again later.');
      });
  }

  addUser(data: any) {
    return (this.$company.addUser(this.organizationId, {username: data.email, ...data}))
      .toPromise()
      .then((users: any) => {
        this.$toaster.show('success', 'User', 'The user was added successfully.');
      }).catch((error) => {
        console.error(error);
        if (error?.error?.detail?.includes('user quota exceeded')) {
          this.$toaster.show('error', 'Users', 'User quota exceeded.');
        } else {
          this.$toaster.show('error', 'Users', 'An error occurred, please try again later.');
        }
      });
  }

  deleteUser(userID) {
    return (this.$company.removeUser(this.organizationId, userID))
      .toPromise()
      .then((users: any) => {
        this.$toaster.show('success', 'User', 'The user was removed successfully.');
      }).catch((error) => {
        console.error(error);
        this.handleUserDeletionError(error, userID);
      });
  }

  private handleUserDeletionError(error: any, userID: string) {
    // Parse the error to determine the type
    const errorMessage = error?.error?.detail || error?.message || '';
    let errorType: UserDeletionError['type'] = 'general';

    if (errorMessage.includes('primary contact')) {
      errorType = 'primary_contact';
    } else if (errorMessage.includes('Company Administrator')) {
      errorType = 'organization_admin';
    } else if (errorMessage.includes('cannot delete your own user')) {
      errorType = 'self_deletion';
    }

    // Get user information for the modal
    this.$company.users(this.organizationId).toPromise()
      .then((users: any[]) => {
        const user = users.find(u => u.id === userID);

        const userDeletionError: UserDeletionError = {
          type: errorType,
          message: errorMessage,
          user: user || { id: userID, first_name: 'Unknown', last_name: 'User' },
          clientCount: this.extractClientCount(errorMessage)
        };

        this.showUserDeletionErrorModal(userDeletionError);
      })
      .catch(() => {
        // Fallback if we can't get user info
        const userDeletionError: UserDeletionError = {
          type: errorType,
          message: errorMessage,
          user: { id: userID, first_name: 'Unknown', last_name: 'User' }
        };

        this.showUserDeletionErrorModal(userDeletionError);
      });
  }

  private extractClientCount(errorMessage: string): number {
    // Try to extract client count from error message
    const match = errorMessage.match(/(\d+)\s+client/);
    return match ? parseInt(match[1], 10) : 1;
  }

  private showUserDeletionErrorModal(error: UserDeletionError) {
    const modalRef = this.$modal.open(UserDeletionErrorModalComponent, {
      size: 'lg',
      container: 'nb-layout',
      keyboard: false,
      backdrop: 'static',
      windowClass: 'user-deletion-error-modal'
    });

    modalRef.componentInstance.error = error;

    modalRef.result.then((result) => {
      if (result === 'reassigned') {
        // Refresh the data grid to reflect changes
        this.dataSource.reload();
        this.$toaster.show('info', 'Info', 'You can now try deleting the user again.');
      }
    }).catch(() => {
      // Modal was dismissed
    });
  }

  resetPassword(user) {
    Swal.fire({
      title: 'Are you sure?',
      text: `${user.first_name} will receive an email with instructions to reset the account password.`,
      icon: 'question',
      showCancelButton: true,
      buttonsStyling: false,
      confirmButtonText: 'Confirm',
      customClass: {
        confirmButton: 'btn btn-primary mr-2',
        cancelButton: 'btn btn-danger ml-2',
      },
      confirmButtonColor: '#41b146',
      cancelButtonColor: '#c74141',
    }).then((result) => {
      if (result.value) {
        this.$account.adminResetPassword(user.username)
        .subscribe(() => {
          this.$toaster.show('success', '', `Password reset instructions have been emailed to ${user.email}.`);
        }, (error) => {
          console.error(error);
          this.$toaster.show('error', 'Error', 'An error occurred, please try again later.');
        });
      }
    });
  }

  canEdit() {
    return ['Lasgro Admin', 'Company Admin'].includes(this.role);
  }

  getExportFileName() {
    return this.company.name?.concat(' users') || 'users';
  }
}
